/**
 * src/pages/respond/OffsiteOffer.js — a travel agent's / venue's offer for an
 * offsite on the vendor quote form.
 *
 * "Can you host this event?" then one card per property option (up to 8):
 *   the property     name, location, type, website / brochure link
 *   dates            one or more date options (Airbnb range picker + a note)
 *   rooms & meals    room rates per room type asked for; the meal plans the
 *                    property really offers (the ones we asked for start
 *                    ticked — untick one and we say it will be passed on),
 *                    with a rate per plan
 *   event costs      DJ, DJ with licence, licence, banquet hall, plus rows of
 *                    their own (quick-add chips for common MICE costs)
 *   GST              prices include GST or are + GST (no package total:
 *                    we compare the individual prices)
 *   more (folded)    hall capacity, AV, quote validity, cancellation,
 *                    payment terms, notes
 */
import React, { useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { AIR } from '../../styles/airTheme';
import AirDatePicker, { formatDay } from '../../components/AirDatePicker';

export const MEAL_PLANS = [['CP', 'CP', 'Breakfast'], ['MAP', 'MAP', 'Breakfast + 1 meal'], ['AP', 'AP', 'All meals']];
export const AV = [['projector', 'Projector / LED screen'], ['collar_mics', 'Collar mics'], ['stage', 'Stage'], ['sound_system', 'Sound system'], ['wifi', 'Dedicated Wi-Fi']];
export const PROPERTY_TYPES = [['5_star', '5 star'], ['4_star', '4 star'], ['3_star', '3 star'], ['villa', 'Villa'], ['homestay', 'Home stay']];
export const ROOMS = [['single', 'Single'], ['double', 'Double'], ['triple', 'Triple'], ['quad', 'Quad']];
const COSTS = [['dj', 'DJ'], ['djLicence', 'DJ with licence'], ['licence', 'Licence'], ['banquetHall', 'Banquet hall']];
const QUICK_ADD = ['Gala dinner (per person)', 'Bar package (per person)', 'Hi-tea (per person)', 'Airport transfers', 'Decor & branding', 'Team-building activity', 'Bonfire', 'Extra bed'];
const PLAN_NAME = Object.fromEntries(MEAL_PLANS.map(([k, , d]) => [k, `${k} (${d.toLowerCase()})`]));
const MAX_OPTIONS = 8;

export const amount = (v) => String(v || '').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

export const emptyOption = (brief = {}) => ({
  propertyName: '', propertyType: '', location: '', link: '',
  dates: [{ checkIn: brief.checkIn || '', checkOut: brief.checkOut || '', note: '' }],
  roomRates: {}, mealPlans: [...(brief.mealPlans || [])], mealRates: {},
  costs: {}, extras: [], gst: '',
  hallCapacity: '', av: [], validUntil: '', cancellation: '', paymentTerms: '', notes: '',
});

const str = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => [k, String(v)]));

/** A property option as the server sent it back, turned into form state for editing. */
export const optionFromSent = (p = {}, brief = {}) => {
  const blank = emptyOption(brief);
  return {
    ...blank,
    propertyName: p.propertyName || '', propertyType: p.propertyType || '', location: p.location || '', link: p.link || '',
    dates: p.dates?.length ? p.dates.map((d) => ({ checkIn: d.checkIn || '', checkOut: d.checkOut || '', note: d.note || '' })) : blank.dates,
    roomRates: str(p.roomRates), mealPlans: [...(p.mealPlans || [])], mealRates: str(p.mealRates), costs: str(p.costs),
    extras: (p.extras || []).map((x) => ({ label: x.label || '', cost: x.cost === undefined || x.cost === null ? '' : String(x.cost) })),
    gst: p.gst || '', hallCapacity: p.hallCapacity ? String(p.hallCapacity) : '', av: [...(p.av || [])],
    validUntil: p.validUntil || '', cancellation: p.cancellation || '', paymentTerms: p.paymentTerms || '', notes: p.notes || '',
  };
};

/** The payload for the server: empty rows and rates for unticked plans left out. */
export const offerPayload = (offer) => ({
  available: offer.available,
  properties: offer.available === 'yes' ? offer.properties.map((p) => ({
    ...p,
    dates: p.dates.filter((d) => d.checkIn || d.checkOut),
    mealRates: Object.fromEntries(Object.entries(p.mealRates).filter(([k]) => p.mealPlans.includes(k))),
    extras: p.extras.filter((x) => x.label.trim()),
  })) : [],
});

/** What still has to be filled in, or ''. */
export const offerProblem = (offer) => {
  if (!offer.available) return 'Tell us whether you can host this event.';
  if (offer.available !== 'yes') return '';
  const i = offer.properties.findIndex((p) => !p.propertyName.trim());
  if (i >= 0) return `Add the property name for option ${i + 1}.`;
  const j = offer.properties.findIndex((p) => !p.dates.some((d) => d.checkIn && d.checkOut));
  if (j >= 0) return `Add at least one date option for ${offer.properties[j].propertyName}.`;
  return '';
};

const lbl = { display: 'block', fontFamily: AIR.sans, fontWeight: 600, fontSize: 13, color: AIR.ink, margin: '0 0 6px' };
const small = { ...lbl, fontWeight: 500, color: AIR.muted };
const hint = { fontSize: 13, color: AIR.muted, margin: '-2px 0 8px', lineHeight: 1.5 };
const sectionTitle = { fontFamily: AIR.sans, fontWeight: 600, fontSize: 15, color: AIR.ink, margin: '0 0 10px' };
const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 12 });
const iconBtn = { width: 40, height: 40, flex: 'none', borderRadius: '50%', border: `1px solid ${AIR.line2}`, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: AIR.muted, padding: 0 };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '6px 0', fontFamily: AIR.sans, fontSize: 14, fontWeight: 600, color: AIR.ink, textDecoration: 'underline', cursor: 'pointer' };

const Chip = ({ on, onClick, children }) => (
  <button type="button" className="air-chip" aria-pressed={on} onClick={onClick}>{on && <Check size={14} />}{children}</button>
);

const Section = ({ title, children, last }) => (
  <div style={{ padding: '18px 0', borderBottom: last ? 'none' : `1px solid ${AIR.line}` }}>
    {title && <h4 style={sectionTitle}>{title}</h4>}
    {children}
  </div>
);

const Rupee = ({ id, label, value, onChange, placeholder = '₹' }) => (
  <div>
    <label style={small} htmlFor={id}>{label}</label>
    <input id={id} type="text" inputMode="decimal" value={value || ''} onChange={(e) => onChange(amount(e.target.value))} placeholder={placeholder} />
  </div>
);

/** One property option. */
const PropertyCard = ({ n, brief, p, onChange, onRemove }) => {
  const [more, setMore] = useState(false);
  const set = (patch) => onChange({ ...p, ...patch });
  const id = (k) => `o${n}-${k}`;
  const roomTypes = ROOMS.filter(([k]) => brief.rooms?.[k]);
  const asked = brief.mealPlans || [];
  const missingPlans = asked.filter((m) => !p.mealPlans.includes(m));
  const askedStay = brief.checkIn && brief.checkOut ? `${formatDay(brief.checkIn)} – ${formatDay(brief.checkOut)}` : '';
  const setDate = (i, patch) => set({ dates: p.dates.map((d, k) => (k === i ? { ...d, ...patch } : d)) });
  const setExtra = (i, patch) => set({ extras: p.extras.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const quick = QUICK_ADD.filter((q) => !p.extras.some((x) => x.label === q));
  const moreCount = [p.hallCapacity, p.av.length, p.validUntil, p.cancellation, p.paymentTerms, p.notes].filter(Boolean).length;

  return (
    <section aria-label={`Property option ${n}`} style={{ background: '#fff', border: `1px solid ${AIR.line2}`, borderRadius: 16, padding: '6px 24px 10px', marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0 4px' }}>
        <span style={{ width: 32, height: 32, borderRadius: '50%', background: AIR.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14, flex: 'none' }}>{n}</span>
        <h3 style={{ fontFamily: AIR.serif, fontWeight: 400, fontSize: 24, color: AIR.ink, margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.propertyName.trim() || `Property option ${n}`}
        </h3>
        {onRemove && <button type="button" style={iconBtn} onClick={onRemove} aria-label={`Remove option ${n}`} title="Remove this option"><Trash2 size={16} /></button>}
      </div>

      <Section>
        <div style={{ ...grid(220), marginBottom: 14 }}>
          <div>
            <label style={lbl} htmlFor={id('name')}>Property name *</label>
            <input id={id('name')} type="text" value={p.propertyName} onChange={(e) => set({ propertyName: e.target.value })} maxLength={200} placeholder="e.g. Coorg Hills Resort" />
          </div>
          <div>
            <label style={lbl} htmlFor={id('loc')}>Location</label>
            <input id={id('loc')} type="text" value={p.location} onChange={(e) => set({ location: e.target.value })} maxLength={200}
              placeholder={brief.destinations?.length ? `e.g. ${brief.destinations[0]}` : 'Town, state'} />
          </div>
          <div>
            <label style={lbl} htmlFor={id('link')}>Website or brochure link</label>
            <input id={id('link')} type="text" value={p.link} onChange={(e) => set({ link: e.target.value })} maxLength={500} placeholder="https://" />
          </div>
        </div>
        <span style={lbl}>Type of property</span>
        <div role="group" aria-label={`Type of property for option ${n}`} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PROPERTY_TYPES.map(([k, label]) => <Chip key={k} on={p.propertyType === k} onClick={() => set({ propertyType: p.propertyType === k ? '' : k })}>{label}</Chip>)}
        </div>
      </Section>

      <Section title="Dates you can offer *">
        <p style={hint}>{askedStay ? `We asked for ${askedStay}. ` : ''}Add every date option that works, with a note if rates differ.</p>
        {p.dates.map((d, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <AirDatePicker range labels={['Check-in', 'Check-out']} value={[d.checkIn, d.checkOut]} onChange={([checkIn, checkOut]) => setDate(i, { checkIn, checkOut })} />
            </div>
            <input type="text" aria-label={`Note for date option ${i + 1}`} value={d.note} onChange={(e) => setDate(i, { note: e.target.value })} maxLength={300}
              placeholder="Note, e.g. weekend rates" style={{ flex: '1 1 200px', minWidth: 0 }} />
            {p.dates.length > 1 && <button type="button" style={iconBtn} onClick={() => set({ dates: p.dates.filter((_, k) => k !== i) })} aria-label={`Remove date option ${i + 1}`}><Trash2 size={16} /></button>}
          </div>
        ))}
        {p.dates.length < 10 && (
          <button type="button" style={linkBtn} onClick={() => set({ dates: [...p.dates, { checkIn: '', checkOut: '', note: '' }] })}><Plus size={16} /> Add another date option</button>
        )}
      </Section>

      <Section title="Rooms & meals">
        <span style={lbl}>Room rate (₹ per room per night)</span>
        <div style={{ ...grid(150), marginBottom: 18 }}>
          {(roomTypes.length ? roomTypes : ROOMS).map(([k, label]) => (
            <Rupee key={k} id={id(`r-${k}`)} label={`${label}${brief.rooms?.[k] ? ` · ${brief.rooms[k]} rooms` : ''}`} value={p.roomRates[k]}
              onChange={(v) => set({ roomRates: { ...p.roomRates, [k]: v } })} />
          ))}
        </div>

        <span style={lbl}>Meal plans this property offers</span>
        {asked.length > 0 && <p style={hint}>We asked for {asked.join(', ')}. Untick any plan the property doesn't offer.</p>}
        <div role="group" aria-label={`Meal plans for option ${n}`} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {MEAL_PLANS.map(([k, short, desc]) => (
            <Chip key={k} on={p.mealPlans.includes(k)} onClick={() => set({ mealPlans: p.mealPlans.includes(k) ? p.mealPlans.filter((x) => x !== k) : [...p.mealPlans, k] })}>
              {short} <span style={{ fontWeight: 400, opacity: 0.75 }}>· {desc}</span>
            </Chip>
          ))}
        </div>
        {missingPlans.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fff8ec', color: '#8a5a00', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
            <AlertTriangle size={16} style={{ flex: 'none', marginTop: 1 }} />
            <span>No {missingPlans.map((m) => PLAN_NAME[m]).join(' or ')} here. We'll let the Marqland team know{p.mealPlans.length ? ` this property offers ${p.mealPlans.join(', ')} instead` : ''}.</span>
          </div>
        )}
        {p.mealPlans.length > 0 && (
          <div style={grid(150)}>
            {MEAL_PLANS.filter(([k]) => p.mealPlans.includes(k)).map(([k]) => (
              <Rupee key={k} id={id(`m-${k}`)} label={`${k} · ₹ per person per day`} value={p.mealRates[k]} onChange={(v) => set({ mealRates: { ...p.mealRates, [k]: v } })} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Event costs (₹ each)">
        <div style={{ ...grid(150), marginBottom: 12 }}>
          {COSTS.map(([k, label]) => <Rupee key={k} id={id(`c-${k}`)} label={label} value={p.costs[k]} onChange={(v) => set({ costs: { ...p.costs, [k]: v } })} />)}
        </div>
        {p.extras.map((x, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <input type="text" aria-label={`Cost item ${i + 1}`} value={x.label} onChange={(e) => setExtra(i, { label: e.target.value })} maxLength={200} placeholder="What it is" style={{ flex: '2 1 0', minWidth: 0 }} />
            <input type="text" inputMode="decimal" aria-label={`Cost of item ${i + 1}`} value={x.cost} onChange={(e) => setExtra(i, { cost: amount(e.target.value) })} placeholder="₹" style={{ flex: '1 1 0', minWidth: 0 }} />
            <button type="button" style={iconBtn} onClick={() => set({ extras: p.extras.filter((_, k) => k !== i) })} aria-label={`Remove cost item ${i + 1}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {p.extras.length < 30 && (
          <>
            <button type="button" style={linkBtn} onClick={() => set({ extras: [...p.extras, { label: '', cost: '' }] })}><Plus size={16} /> Add a cost</button>
            {quick.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 13, color: AIR.muted, marginRight: 2 }}>Quick add:</span>
                {quick.map((q) => (
                  <button key={q} type="button" onClick={() => set({ extras: [...p.extras, { label: q, cost: '' }] })}
                    style={{ border: `1px dashed ${AIR.field}`, background: '#fff', borderRadius: 40, padding: '5px 12px', fontFamily: AIR.sans, fontSize: 13, color: AIR.ink, cursor: 'pointer' }}>
                    + {q}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      <Section>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...lbl, margin: 0 }}>Prices are</span>
          <div role="group" aria-label={`GST for option ${n}`} style={{ display: 'flex', gap: 8 }}>
            <Chip on={p.gst === 'included'} onClick={() => set({ gst: p.gst === 'included' ? '' : 'included' })}>GST included</Chip>
            <Chip on={p.gst === 'extra'} onClick={() => set({ gst: p.gst === 'extra' ? '' : 'extra' })}>+ GST</Chip>
          </div>
        </div>
      </Section>

      <Section last>
        <button type="button" style={{ ...linkBtn, textDecoration: 'none' }} aria-expanded={more} onClick={() => setMore(!more)}>
          <ChevronDown size={16} style={{ transform: more ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          More details <span style={{ fontWeight: 400, color: AIR.muted }}>(optional{moreCount ? ` · ${moreCount} added` : ': hall size, AV, validity, cancellation, payment terms'})</span>
        </button>
        {more && (
          <div style={{ paddingTop: 12 }}>
            <div style={{ ...grid(220), marginBottom: 16 }}>
              <div>
                <label style={lbl} htmlFor={id('hall')}>Largest hall seats</label>
                <input id={id('hall')} type="text" inputMode="numeric" value={p.hallCapacity} onChange={(e) => set({ hallCapacity: amount(e.target.value) })} placeholder={brief.pax ? `We have ${brief.pax} pax` : 'People'} />
              </div>
              <div>
                <span style={lbl}>Quote valid until</span>
                <AirDatePicker label="Quote valid until" value={p.validUntil} onChange={(validUntil) => set({ validUntil })} />
              </div>
            </div>
            <span style={lbl}>AV the property can provide</span>
            {brief.av?.length > 0 && <p style={hint}>We need: {brief.av.map((a) => AV.find(([k]) => k === a)?.[1] || a).join(', ')}</p>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {AV.map(([k, label]) => <Chip key={k} on={p.av.includes(k)} onClick={() => set({ av: p.av.includes(k) ? p.av.filter((x) => x !== k) : [...p.av, k] })}>{label}</Chip>)}
            </div>
            <div style={{ ...grid(260), marginBottom: 16 }}>
              <div>
                <label style={lbl} htmlFor={id('cancel')}>Cancellation policy</label>
                <textarea id={id('cancel')} rows={2} value={p.cancellation} onChange={(e) => set({ cancellation: e.target.value })} maxLength={2000} placeholder="e.g. Free until 30 days before" style={{ minHeight: 72 }} />
              </div>
              <div>
                <label style={lbl} htmlFor={id('pay')}>Payment terms</label>
                <textarea id={id('pay')} rows={2} value={p.paymentTerms} onChange={(e) => set({ paymentTerms: e.target.value })} maxLength={2000} placeholder="e.g. 50% to confirm, rest 7 days before" style={{ minHeight: 72 }} />
              </div>
            </div>
            <label style={lbl} htmlFor={id('notes')}>Anything else about this property</label>
            <textarea id={id('notes')} rows={2} value={p.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={3000} placeholder="Lawn for the gala dinner, parking, distance from the airport…" style={{ minHeight: 72 }} />
          </div>
        )}
      </Section>
    </section>
  );
};

export default function OffsiteOffer({ brief, offer, setOffer }) {
  const setProps = (properties) => setOffer({ ...offer, properties });
  return (
    <>
      <section style={{ background: '#fff', border: `1px solid ${AIR.line2}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <h2 style={{ fontFamily: AIR.serif, fontWeight: 400, fontSize: 28, color: AIR.ink, margin: '0 0 4px' }}>Your options</h2>
        <p style={{ fontSize: 14, color: AIR.muted, margin: '0 0 14px' }}>Send as many properties as you like. Only the property name and one date option are required.</p>
        <span style={lbl}>Can you host this event? *</span>
        <div role="group" aria-label="Can you host this event?" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Chip on={offer.available === 'yes'} onClick={() => setOffer({ ...offer, available: 'yes' })}>Yes, here are my options</Chip>
          <Chip on={offer.available === 'no'} onClick={() => setOffer({ ...offer, available: 'no' })}>Sorry, not this time</Chip>
        </div>
      </section>

      {offer.available === 'yes' && (
        <>
          {offer.properties.map((p, i) => (
            <PropertyCard key={i} n={i + 1} brief={brief} p={p}
              onChange={(next) => setProps(offer.properties.map((x, k) => (k === i ? next : x)))}
              onRemove={offer.properties.length > 1 ? () => setProps(offer.properties.filter((_, k) => k !== i)) : null} />
          ))}
          {offer.properties.length < MAX_OPTIONS && (
            <button type="button" onClick={() => setProps([...offer.properties, emptyOption(brief)])}
              style={{ width: '100%', border: `1.5px dashed ${AIR.field}`, background: '#fff', borderRadius: 16, padding: '18px 16px', marginBottom: 16, cursor: 'pointer', fontFamily: AIR.sans, fontSize: 15, fontWeight: 600, color: AIR.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Plus size={18} /> Add another property
            </button>
          )}
        </>
      )}
    </>
  );
}
